import { Box, Center, Checkbox, Divider, Group, Loader, Modal, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { tKeys, useTranslate } from 'locales/index';
import type { UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useMemo, useState } from 'react';
import { firstBy, omit } from 'remeda';
import type { ReminderDocumentType } from '../api/preview-reminder-request';
import { useLoadDebtorSendingOptions } from '../api/use-load-debtor-sending-options';
import { useLoadReminderTemplates } from '../api/use-load-reminder-templates';
import { usePreviewReminderEmail } from '../api/use-preview-reminder-email';
import { usePreviewReminderPdf } from '../api/use-preview-reminder-pdf';
import type { SendAdditionalReminderVariables } from '../api/use-send-additional-reminder';
import { ReminderAddressForm } from './ReminderAddressForm';
import { ReminderChannels } from './ReminderChannels';
import { type ReminderEmail, ReminderEmailRecipients } from './ReminderEmailRecipients';
import { ReminderInvoiceSelection } from './ReminderInvoiceSelection';
import { type ReminderAddress, ReminderPostalAddresses } from './ReminderPostalAddresses';
import { ReminderTemplateSelect } from './ReminderTemplateSelect';
import { ReminderWizardPreview } from './ReminderWizardPreview';
import { ReminderWizardShell } from './ReminderWizardShell';

type ReminderWizardValues = {
  invoiceIds: Array<string>;
  creditNoteIds: Array<string>;
  accountingPaymentIds: Array<string>;
  tpPaymentIds: Array<string>;
  addLateFees: boolean;
  sendEmail: boolean;
  sendPost: boolean;
  sendRegisteredPost: boolean;
  emails: Array<ReminderEmail>;
  addresses: Array<ReminderAddress>;
  templateId: string;
};

type ReminderWizardFormProps = {
  debtorId: number;
  invoices: Array<UnhandledInvoice>;
  templateId: string;
  documentType: ReminderDocumentType;
  defaultEmails: Array<string>;
  defaultAddresses: Array<ReminderAddress>;
  onSubmit: (variables: SendAdditionalReminderVariables) => void | Promise<void>;
  onCancel: () => void;
};

const PREVIEW_DEBOUNCE_MS = 700;

const isEligible = (invoice: UnhandledInvoice, documentType: ReminderDocumentType) =>
  invoice.availableActions?.includes(documentType) ?? false;

const ReminderWizardForm = ({
  debtorId,
  invoices,
  templateId,
  documentType,
  defaultEmails,
  defaultAddresses,
  onSubmit,
  onCancel,
}: ReminderWizardFormProps) => {
  const t = useTranslate();
  const { reminderTemplates } = useLoadReminderTemplates(documentType);

  const form = useForm<ReminderWizardValues>({
    mode: 'controlled',
    initialValues: {
      invoiceIds: invoices
        .filter(invoice => isEligible(invoice, documentType))
        .map(invoice => invoice.id),
      creditNoteIds: [],
      accountingPaymentIds: [],
      tpPaymentIds: [],
      addLateFees: false,
      sendEmail: false,
      sendPost: false,
      sendRegisteredPost: documentType === 'formal_notice',
      emails: defaultEmails.map(value => ({ value, selected: true })),
      addresses: defaultAddresses.map(address => ({ ...address, selected: true })),
      templateId,
    },
  });

  const selectedEligible = invoices.filter(
    invoice => isEligible(invoice, documentType) && form.values.invoiceIds.includes(invoice.id),
  );
  const mainInvoice = firstBy(selectedEligible, invoice => invoice.dueDate.valueOf());
  const selectedTemplateId = form.values.templateId || (reminderTemplates[0]?.id ?? '');
  const hasChannel =
    form.values.sendEmail || form.values.sendPost || form.values.sendRegisteredPost;
  const hasSelectedEmail = form.values.emails.some(email => email.selected);
  const hasSelectedAddress = form.values.addresses.some(address => address.selected);
  const canSend =
    hasChannel &&
    (!form.values.sendEmail || hasSelectedEmail) &&
    (!(form.values.sendPost || form.values.sendRegisteredPost) || hasSelectedAddress);

  const previewVariables = useMemo(
    () => ({
      invoiceId: mainInvoice?.id ?? '',
      templateId: selectedTemplateId,
      documentType,
      addLateFees: form.values.addLateFees,
      additionalInvoiceIds: mainInvoice
        ? form.values.invoiceIds.filter(id => id !== mainInvoice.id)
        : [],
      creditNoteIds: form.values.creditNoteIds,
      accountingPaymentIds: form.values.accountingPaymentIds,
      tpPaymentIds: form.values.tpPaymentIds,
    }),
    [
      mainInvoice,
      documentType,
      selectedTemplateId,
      form.values.addLateFees,
      form.values.invoiceIds,
      form.values.creditNoteIds,
      form.values.accountingPaymentIds,
      form.values.tpPaymentIds,
    ],
  );
  const [activePreviewTab, setActivePreviewTab] = useState('email');
  const [isSending, setIsSending] = useState(false);
  const [debouncedPreviewVariables] = useDebouncedValue(previewVariables, PREVIEW_DEBOUNCE_MS);
  const canPreview = Boolean(
    debouncedPreviewVariables.invoiceId && debouncedPreviewVariables.templateId,
  );
  const { reminderEmailPreview, isReminderEmailPreviewInitialLoading } = usePreviewReminderEmail(
    debouncedPreviewVariables,
    { enabled: canPreview },
  );
  const { reminderPdfPreview, isReminderPdfPreviewInitialLoading } = usePreviewReminderPdf(
    debouncedPreviewVariables,
    { enabled: canPreview && activePreviewTab === 'pdf' },
  );

  const [editedAddress, setEditedAddress] = useState<{
    index?: number;
    address?: ReminderAddress;
  } | null>(null);

  const toggleId =
    (field: 'invoiceIds' | 'creditNoteIds' | 'accountingPaymentIds' | 'tpPaymentIds') =>
    (id: string) =>
      form.setFieldValue(field, ids =>
        ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id],
      );
  const toggleInvoice = toggleId('invoiceIds');
  const toggleCreditNote = toggleId('creditNoteIds');
  const togglePayment = (id: string, type: 'accounting' | 'third_party') =>
    toggleId(type === 'accounting' ? 'accountingPaymentIds' : 'tpPaymentIds')(id);

  const saveAddress = (address: ReminderAddress) => {
    if (editedAddress?.index === undefined)
      form.insertListItem('addresses', { ...address, selected: true });
    else form.setFieldValue(`addresses.${editedAddress.index}`, address);
    setEditedAddress(null);
  };

  const toggleAddress = (index: number) =>
    form.setFieldValue(`addresses.${index}.selected`, !form.values.addresses[index]?.selected);

  const toggleEmail = (index: number) =>
    form.setFieldValue(`emails.${index}.selected`, !form.values.emails[index]?.selected);
  const addEmail = (value: string) => form.insertListItem('emails', { value, selected: true });

  const handleSend = () => {
    if (!mainInvoice) return;
    setIsSending(true);
    Promise.resolve(
      onSubmit({
        invoiceId: mainInvoice.id,
        additionalInvoiceIds: form.values.invoiceIds.filter(id => id !== mainInvoice.id),
        creditNoteIds: form.values.creditNoteIds,
        accountingPaymentIds: form.values.accountingPaymentIds,
        tpPaymentIds: form.values.tpPaymentIds,
        sendingOptions: {
          templateId: selectedTemplateId,
          addLateFees: form.values.addLateFees,
          sendEmail: form.values.sendEmail,
          sendPost: form.values.sendPost,
          sendRegisteredPost: form.values.sendRegisteredPost,
          emails: form.values.emails
            .filter(email => email.selected)
            .map(email => ({ value: email.value })),
          addressesAttributes: form.values.addresses
            .filter(address => address.selected)
            .map(address => omit(address, ['selected'])),
        },
      }),
    )
      // failures surface via the global MutationCache; here we only reset the button
      .catch(() => {})
      .finally(() => setIsSending(false));
  };

  const step1 = (
    <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
      <Group align="flex-end" wrap="nowrap">
        <Box style={{ flex: 1 }}>
          <ReminderTemplateSelect
            templates={reminderTemplates}
            value={selectedTemplateId}
            defaultTemplateId={templateId}
            onChange={id => form.setFieldValue('templateId', id)}
          />
        </Box>
        {documentType === 'additional_reminder' && (
          // Matches the select input's height (Mantine "sm") so the checkbox
          // centers against the input itself rather than the label + input block.
          <Center h={36}>
            <Checkbox
              label={t(tKeys.LATE_FEES_INCLUDED)}
              {...form.getInputProps('addLateFees', { type: 'checkbox' })}
            />
          </Center>
        )}
      </Group>
      <Divider />
      <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <ReminderInvoiceSelection
          debtorId={debtorId}
          documentType={documentType}
          selectedIds={form.values.invoiceIds}
          onToggle={toggleInvoice}
          selectedCreditNoteIds={form.values.creditNoteIds}
          onToggleCreditNote={toggleCreditNote}
          selectedAccountingPaymentIds={form.values.accountingPaymentIds}
          selectedTpPaymentIds={form.values.tpPaymentIds}
          onTogglePayment={togglePayment}
        />
      </Box>
    </Stack>
  );

  const step2 = (
    <Stack gap="md" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <ReminderChannels
        documentType={documentType}
        value={{
          sendEmail: form.values.sendEmail,
          sendPost: form.values.sendPost,
          sendRegisteredPost: form.values.sendRegisteredPost,
        }}
        onChange={channels => form.setValues(channels)}
      />
      {form.values.sendEmail && (
        <ReminderEmailRecipients
          emails={form.values.emails}
          onToggle={toggleEmail}
          onAdd={addEmail}
        />
      )}
      {(form.values.sendPost || form.values.sendRegisteredPost) && (
        <ReminderPostalAddresses
          addresses={form.values.addresses}
          onAdd={() => setEditedAddress({})}
          onEdit={index => setEditedAddress({ index, address: form.values.addresses[index] })}
          onToggle={toggleAddress}
        />
      )}
    </Stack>
  );

  return (
    <>
      <ReminderWizardShell
        step1={step1}
        step2={step2}
        preview={
          <ReminderWizardPreview
            email={reminderEmailPreview.email}
            pdf={reminderPdfPreview.pdf}
            isEmailLoading={isReminderEmailPreviewInitialLoading}
            isPdfLoading={isReminderPdfPreviewInitialLoading}
            activeTab={activePreviewTab}
            onTabChange={next => setActivePreviewTab(next ?? 'email')}
          />
        }
        onCancel={onCancel}
        onSend={handleSend}
        canContinue={Boolean(mainInvoice)}
        canSend={canSend}
        isSending={isSending}
      />

      <Modal
        opened={editedAddress !== null}
        onClose={() => setEditedAddress(null)}
        title={t(tKeys.FORM.ADDRESS.ADD)}
        zIndex={1000}
      >
        {editedAddress !== null && (
          <ReminderAddressForm
            initialAddress={editedAddress.address}
            onSubmit={saveAddress}
            onCancel={() => setEditedAddress(null)}
          />
        )}
      </Modal>
    </>
  );
};

type ReminderWizardProps = Omit<ReminderWizardFormProps, 'defaultEmails' | 'defaultAddresses'>;

export const ReminderWizard = (props: ReminderWizardProps) => {
  const t = useTranslate();
  const { debtorSendingOptions, isDebtorSendingOptionsInitialLoading } =
    useLoadDebtorSendingOptions({ debtorId: props.debtorId });

  if (isDebtorSendingOptionsInitialLoading) {
    // render the shell at its final footprint so the modal does not resize once loaded
    return (
      <ReminderWizardShell
        step1={
          <Center style={{ flex: 1 }}>
            <Loader role="progressbar" aria-label={t(tKeys.LOADING)} />
          </Center>
        }
        step2={null}
        preview={<ReminderWizardPreview isEmailLoading activeTab="email" onTabChange={() => {}} />}
        onCancel={props.onCancel}
        onSend={() => {}}
        canContinue={false}
        canSend={false}
      />
    );
  }

  return (
    <ReminderWizardForm
      {...props}
      defaultEmails={debtorSendingOptions.emails}
      defaultAddresses={debtorSendingOptions.addresses}
    />
  );
};
